import {
  createSlice,
  createAsyncThunk,
  createDraftSafeSelector,
} from '@reduxjs/toolkit';
import axios from 'axios';

import { api } from '../../../api/api';
import { selectProjectUuid } from '../../Project/projectSlice';
import FieldFactory from '../../SharedField/FieldFactory';

export const loadTasks = createAsyncThunk(
  'executions/loadTasks',
  async (projectID, thunkApi) => {
    const response = await api.tasks(projectID);
    return response;
  },
);

export const fetchTask = createAsyncThunk(
  'executions/fetchTask',
  async (taskID, thunkApi) => {
    thunkApi.dispatch(resetTaskEdit());
    const projectID = selectProjectUuid(thunkApi.getState());
    const response = await api.tasks.getOne(projectID, taskID);
    return response;
  },
);

export const executeTask = createAsyncThunk(
  'tasks/executeTask',
  async (taskID, thunkApi) => {
    const projectID = selectProjectUuid(thunkApi.getState());
    const response = await api.tasks.execute(projectID, taskID);
    return response;
  },
);

export const deleteTask = createAsyncThunk(
  'sharedField/deleteTask',
  async (taskID, thunkApi) => {
    const projectID = selectProjectUuid(thunkApi.getState());
    await api.tasks.delete(projectID, taskID);
  },
);

export const fetchTaskDependencies = createAsyncThunk(
  'sharedField/fetchTaskDependencies',
  async (taskID, thunkApi) => {
    const projectID = selectProjectUuid(thunkApi.getState());
    return await api.tasks.dependencies(projectID, taskID);
  },
);

export const loadTaskHelp = createAsyncThunk(
  'tasks/loadTaskHelp',
  async (mdFiles) => {
    const entries = Object.entries(mdFiles || {});
    if (!entries.length) {
      throw new Error('No documentation was provided for this task.');
    }
    const results = await Promise.all(
      entries.map(([key, value]) =>
        axios.get(value.url).then((resp) => [key, resp.data]),
      ),
    );
    return Object.fromEntries(results);
  },
);

// selectors
const selectSelf = (state) => state.tasks;
export const selectTasks = createDraftSafeSelector(
  selectSelf,
  (tasks) => tasks.tasks,
);
export const selectTasksLoading = createDraftSafeSelector(
  selectSelf,
  (tasks) => tasks.loading,
);
export const selectNewTaskParameters = createDraftSafeSelector(
  selectSelf,
  (tasks) => tasks.newTaskParameters,
);
export const selectNewTaskParametersSet = createDraftSafeSelector(
  selectSelf,
  (tasks) => tasks.newTaskParametersSet,
);
export const selectNewTaskManyParameters = createDraftSafeSelector(
  selectSelf,
  (tasks) => tasks.newTaskManyParameters,
);
export const selectTaskCursor = createDraftSafeSelector(
  selectSelf,
  (tasks) => tasks.taskCursor,
);
export const selectTaskEdit = createDraftSafeSelector(
  selectSelf,
  (tasks) => tasks.taskEdit,
);
export const selectTaskEditSet = createDraftSafeSelector(
  selectSelf,
  (tasks) => tasks.taskEditSet,
);
export const selectTaskEditManyParameters = createDraftSafeSelector(
  selectSelf,
  (tasks) => tasks.taskEditManyParameters,
);
export const selectTaskEditValidations = createDraftSafeSelector(
  selectSelf,
  (tasks) => tasks.taskEditValidations,
);
export const selectTaskDelete = createDraftSafeSelector(
  selectSelf,
  (tasks) => tasks.taskDelete,
);
export const selectTaskExecutions = createDraftSafeSelector(
  selectSelf,
  (tasks) => tasks.executions,
);
export const selectTaskDependencies = createDraftSafeSelector(
  selectSelf,
  (tasks) => tasks.taskDependencies,
);
export const selectTaskHelp = createDraftSafeSelector(
  selectSelf,
  (tasks) => tasks.taskHelp,
);

// `'default' in parameter` rather than a truthiness check, so a declared default of 0,
// false or '' survives -- the same test Connections/CreateConnection.js uses.
function resolveParameterDefault(parameter) {
  if ('default' in parameter) return parameter.default;
  return FieldFactory.getDefaultValue(parameter.type);
}

// A parameter that is `many` in one action but scalar in another would otherwise carry a
// string into ManyParameter, which maps over it.
function coerceParameterValue(parameter, value) {
  if (!parameter.many) return value;
  return Array.isArray(value) ? value : [];
}

function touchAll(values) {
  const touched = {};
  for (const name in values) touched[name] = true;
  return touched;
}

const tasksSlice = createSlice({
  name: 'tasks',
  initialState: {
    tasks: [],
    loading: false,
    newTaskParameters: {},
    newTaskParametersTouched: {},
    newTaskParametersSet: false,
    newTaskManyParameters: {},
    taskCursor: null,
    taskEdit: {},
    taskEditTouched: {},
    taskEditSet: false,
    taskEditManyParameters: {},
    taskEditValidations: {},
    taskDelete: {
      deleting: false,
      error: null,
    },
    executions: {},
    taskDependencies: {
      loading: false,
      error: null,
      data: null,
    },
    taskHelp: {
      open: false,
      // parameter header id
      parameterHeaderId: '',
      actionKey: '',
      // documentation for each action
      // action -> markdown
      documentation: {},
      loading: false,
      error: null,
    },
  },

  reducers: {
    setTaskHelpVisible(state, action) {
      state.taskHelp.open = action.payload;
    },
    setTaskHelpParameterHeaderId(state, action) {
      state.taskHelp.parameterHeaderId = action.payload;
      state.taskHelp.open = true;
    },
    setTaskHelpActionKey(state, action) {
      state.taskHelp.actionKey = action.payload;
      state.taskHelp.open = false;
    },
    updateTaskHelp(state, action) {
      state.taskHelp = { ...state.taskHelp, ...action.payload };
    },
    changeNewTaskParameterValue(state, action) {
      const { name, value } = action.payload;
      state.newTaskParameters[name] = value;
      state.newTaskParametersTouched[name] = true;
    },
    configureNewTaskParameters(state, action) {
      action.payload.forEach((param) => {
        param.parameters.forEach((p) => {
          // A value the user typed follows them across actions; anything they haven't
          // touched is re-resolved from the action they just picked.
          const value = state.newTaskParametersTouched[p.name]
            ? state.newTaskParameters[p.name]
            : resolveParameterDefault(p);

          if (p.many) state.newTaskManyParameters[p.name] = p;
          state.newTaskParameters[p.name] = coerceParameterValue(p, value);
        });
      });

      state.newTaskParametersSet = true;
    },

    resetNewTaskParameters(state) {
      state.newTaskParameters = {};
      state.newTaskParametersTouched = {};
      state.newTaskManyParameters = {};
      state.newTaskParametersSet = false;
    },

    // The JSON editor is an explicit edit of every field it shows, so those values have to
    // survive the action change it can trigger.
    hardSetNewParameters(state, action) {
      state.newTaskParameters = action.payload;
      state.newTaskParametersTouched = touchAll(action.payload);
    },

    hardSetEditParameters(state, action) {
      state.taskEdit = action.payload;
      state.taskEditTouched = touchAll(action.payload);
    },

    changeTaskEditValue(state, action) {
      const { name, value } = action.payload;
      state.taskEdit[name] = value;
      state.taskEditTouched[name] = true;
    },
    configureTaskEditParameters(state, action) {
      const { parameters, actionName } = action.payload;
      const saved = state.taskCursor?.parameters ?? {};
      // The saved parameters belong to the action the task was saved with; any other
      // action starts from its own declared defaults.
      const onSavedAction = actionName === saved.action_name;

      parameters.forEach((param) => {
        param.parameters.forEach((p) => {
          let value;
          if (state.taskEditTouched[p.name]) {
            // a value the user typed follows them across actions
            value = state.taskEdit[p.name];
          } else if (onSavedAction && saved[p.name] !== undefined) {
            value = saved[p.name];
          } else {
            value = resolveParameterDefault(p);
          }

          if (p.many) state.taskEditManyParameters[p.name] = p;
          state.taskEdit[p.name] = coerceParameterValue(p, value);
        });
      });

      if (!state.taskEditSet) {
        state.taskEditValidations = {};
        state.taskEditSet = true;
      }
    },
    setTaskEditValidation(state, action) {
      const { name, errors } = action.payload;
      state.taskEditValidations[name] = errors;
    },
    resetTaskEdit(state) {
      state.taskEdit = {};
      state.taskEditTouched = {};
      state.taskEditSet = false;
      state.taskEditManyParameters = {};
      state.taskEditValidations = {};
    },
    resetTasks(state) {
      state.tasks = [];
      state.loading = false;
      state.newTaskParameters = {};
      state.newTaskParametersTouched = {};
      state.newTaskParametersSet = false;
      state.newTaskManyParameters = {};
      state.taskCursor = null;
      state.taskEdit = {};
      state.taskEditTouched = {};
      state.taskEditSet = false;
      state.taskEditManyParameters = {};
      state.taskEditValidations = {};
    },
    resetTaskDependencies(state) {
      state.taskDependencies = {
        loading: false,
        error: null,
        data: null,
      };
    },
  },
  extraReducers: {
    [loadTasks.pending]: (state, action) => {
      state.loading = true;
      state.tasks = [];
    },
    [loadTasks.fulfilled]: (state, action) => {
      state.tasks = action.payload;
      state.loading = false;
    },
    [loadTasks.rejected]: (state, action) => {
      // to reset array or not?
      state.tasks = [];
      state.loading = false;
    },
    [fetchTask.fulfilled]: (state, action) => {
      state.taskCursor = action.payload;
      state.taskEdit = {};
      state.taskEditTouched = {};
    },
    [fetchTask.rejected]: (state, action) => {
      state.taskCursor = null;
    },
    [executeTask.pending]: (state, action) => {
      state.executions[action.meta.arg] = true;
    },
    [executeTask.fulfilled]: (state, action) => {
      const { arg: taskID } = action.meta;
      state.executions[taskID] = false;
    },
    [executeTask.rejected]: (state, action) => {
      state.executions[action.meta.arg] = false;
    },
    [deleteTask.pending]: (state) => {
      state.taskDelete.deleting = true;
      state.taskDelete.error = null;
    },
    [deleteTask.fulfilled]: (state) => {
      state.taskDelete.deleting = false;
      state.taskDelete.error = null;
    },
    [deleteTask.rejected]: (state, action) => {
      state.taskDelete.deleting = false;
      state.taskDelete.error = action.payload;
    },
    [fetchTaskDependencies.pending]: (state) => {
      state.taskDependencies.loading = true;
      state.taskDependencies.error = null;
    },
    [fetchTaskDependencies.fulfilled]: (state, action) => {
      state.taskDependencies.loading = false;
      state.taskDependencies.error = null;
      state.taskDependencies.data = action.payload;
    },
    [fetchTaskDependencies.rejected]: (state, action) => {
      state.taskDependencies.loading = false;
      state.taskDependencies.error = action.error;
    },
    [loadTaskHelp.pending]: (state) => {
      state.taskHelp.loading = true;
      state.taskHelp.error = null;
      state.taskHelp.documentation = {};
    },
    [loadTaskHelp.fulfilled]: (state, action) => {
      state.taskHelp.loading = false;
      state.taskHelp.documentation = action.payload;
    },
    [loadTaskHelp.rejected]: (state, action) => {
      state.taskHelp.loading = false;
      state.taskHelp.error = action.error.message;
    },
  },
});

export const {
  setTaskHelpVisible,
  setTaskHelpParameterHeaderId,
  setTaskHelpActionKey,
  updateTaskHelp,
  changeNewTaskParameterValue,
  configureNewTaskParameters,
  resetNewTaskParameters,
  resetTasks,
  changeTaskEditValue,
  configureTaskEditParameters,
  resetTaskEdit,
  setTaskEditValidation,
  resetTaskDependencies,
  hardSetNewParameters,
  hardSetEditParameters,
} = tasksSlice.actions;

export default tasksSlice.reducer;
